import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Activity,
  BarChart3,
  History,
  CreditCard,
  FlaskConical,
  LogOut,
  Settings2,
  Shield,
  Truck,
  Users,
  WalletCards,
  Pill,
  Stethoscope,
  KeyRound,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';

const NAV = [
  { key: 'dashboard', label: 'Overview', path: '/admin/dashboard', icon: BarChart3 },
  { key: 'operations', label: 'Live Operations', path: '/admin/operations', icon: Activity },
  { key: 'providers', label: 'Providers & KYC', path: '/admin/providers', icon: Stethoscope },
  { key: 'customers', label: 'Customers', path: '/admin/customers', icon: Users },
  { key: 'dispatch', label: 'Dispatch', path: '/admin/provider-dispatch', icon: Truck },
  { key: 'services', label: 'Services', path: '/admin/services', icon: Settings2 },
  { key: 'payments', label: 'Payments', path: '/admin/payment-operations', icon: CreditCard },
  { key: 'finance', label: 'Finance & Payouts', path: '/admin/financial-config', icon: WalletCards },
  { key: 'pharmacy', label: 'Pharmacy', path: '/admin/pharmacy', icon: Pill },
  { key: 'labs', label: 'Lab Operations', path: '/admin/labs', icon: FlaskConical },
  { key: 'referrals', label: 'Referrals', path: '/admin/referrals', icon: Users },
  { key: 'audit', label: 'Security Audit', path: '/admin/audit', icon: History },
  { key: 'team_access', label: 'Team Access', path: '/admin/team', icon: KeyRound, ownerOnly: true },
];


export default function AdminDesktopShell({ children }) {
  const { me, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const permissions = useMemo(() => new Set(me?.permissions || []), [me]);

  const navItems = NAV.filter((item) => {
    if (item.ownerOnly) return !!me?.is_owner;
    return me?.is_owner || permissions.has(item.key);
  });

  const activePath = location.pathname;

  const handleLogout = () => {
    logout();
    navigate('/admin', { replace: true });
  };

  return (
    <div className="admin-portal">
      <aside className="admin-sidebar">
        <div>
          <div className="admin-brand">
            <div className="admin-brand-mark"><Shield size={19} strokeWidth={2.2} /></div>
            <div>
              <div className="admin-brand-title">RESQLY</div>
              <div className="admin-brand-sub">Operations Console</div>
            </div>
          </div>

          <div className="admin-access-pill">
            <Activity size={14} />
            <span>{me?.is_owner ? 'MAIN ACCESS' : 'TEAM ACCESS'}</span>
            <span className="admin-live-dot" />
          </div>

          <nav className="admin-nav" aria-label="Admin navigation">
            <div className="admin-nav-label">Workspace</div>
            {navItems.map(({ key, label, path, icon: Icon }) => {
              const active = key === 'dashboard'
                ? activePath === '/admin/dashboard'
                : activePath.startsWith(path);
              return (
                <Link key={key} to={path} className={`admin-nav-item ${active ? 'is-active' : ''}`}>
                  <Icon size={17} />
                  <span>{label}</span>
                  {active && <motion.span layoutId="admin-nav-glow" className="admin-nav-active-bar" />}
                </Link>
              );
            })}
          </nav>

        </div>

        <div className="admin-sidebar-footer">
          <Link to="/admin/security" className={`admin-profile-card ${activePath === '/admin/security' ? 'is-active' : ''}`}>
            <div className="admin-avatar">{(me?.display_name || me?.username || 'R').slice(0, 1).toUpperCase()}</div>
            <div className="min-w-0">
              <div className="admin-profile-name">{me?.display_name || 'RESQLY Admin'}</div>
              <div className="admin-profile-role">@{me?.username || 'owner'}</div>
            </div>
            <KeyRound size={16} className="ml-auto text-slate-400" />
          </Link>
          <button onClick={handleLogout} className="admin-logout">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <div className="admin-main-glow" />
        <div className="admin-content">{children}</div>
      </main>
    </div>
  );
}
